import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";

import { TextField, TextFieldProps } from "@mui/material";

import { getLocaleSeparators } from "../format/formatNumber";

export type DecimalTextFieldProps = Omit<
  TextFieldProps,
  "value" | "defaultValue" | "onChange" | "customInput" | "color" | "type"
> & {
  decimalScale?: number;
  fixedDecimalScale?: boolean;
  suffix?: string;
  value?: string;
  defaultValue?: number;
  allowNegative?: boolean;
  onChange: (value?: string) => void;
  minValue?: number;
  maxValue?: number;
  disableThousandSeparator?: boolean;
};

/**
 * Shared decimal input built on react-number-format. The value is a numeric string (decimal point is
 * always "."; thousands separators are stripped) so callers can `Number(value)` directly. Grouping and
 * decimal marks for display follow the active i18next language.
 */
export function DecimalTextField(props: DecimalTextFieldProps) {
  const {
    decimalScale,
    suffix,
    onChange,
    value,
    fixedDecimalScale,
    allowNegative,
    minValue,
    maxValue,
    disableThousandSeparator,
    ...textInputFieldProps
  } = props;

  const { i18n } = useTranslation();
  const { decimalSeparator, thousandSeparator } = getLocaleSeparators(i18n.language);

  return (
    <NumericFormat
      suffix={suffix}
      thousandSeparator={disableThousandSeparator ? undefined : thousandSeparator}
      decimalSeparator={decimalSeparator}
      customInput={TextField}
      variant={"outlined"}
      onValueChange={(values) => onChange(values.value)}
      {...textInputFieldProps}
      value={
        value ?? ""
      } /* empty string as workaround for this bug: https://github.com/s-yadav/react-number-format/issues/727 */
      decimalScale={decimalScale ?? 2}
      fixedDecimalScale={fixedDecimalScale}
      valueIsNumericString={true}
      allowNegative={allowNegative}
      fullWidth
      isAllowed={(values) => {
        const { floatValue } = values;
        if (floatValue === undefined) {
          return true;
        }
        return (minValue === undefined || floatValue >= minValue) && (maxValue === undefined || floatValue <= maxValue);
      }}
    />
  );
}
